import { first, isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { useContent } from 'src/contexts/ContentContext';
import * as Tabs from 'src/core/ui/Tabs';
import { useComputedState } from 'src/hooks/useComputedState';
import { ContentId } from 'src/utils/constants/contentId';
import {
  Listing,
  ListingGroupItemInput,
  MergeListingGroupInput,
} from 'src/WebApiController';

import { GroupListingsPreview } from './GroupListingsPreview';
import * as styles from './GroupListingsPreview.css';
import { UngroupedListingGroupId } from './GroupListingsPreviewTable.constants';

type GroupPreviewProps = {
  eventListings: Listing[];
  groups: MergeListingGroupInput[];
};

export const GroupsPreview: React.FC<GroupPreviewProps> = ({
  eventListings,
  groups,
}) => {
  const firstListingGroup = useMemo(
    () => first(groups)?.listingGroupId,
    [groups]
  );
  const [selectedGroup, setSelectedGroup] = useComputedState(firstListingGroup);
  const ungroupedText = useContent(ContentId.Ungrouped);

  const ungroupedListings = useMemo(() => {
    const groupedListingIds = groups.flatMap((group) =>
      group.listingGroupItems
        .filter(({ listingId }) => listingId > 0)
        .map(({ listingId }) => listingId)
    );
    return eventListings.filter(({ id }) => !groupedListingIds.includes(id));
  }, [eventListings, groups]);

  const ungroupedListingGroup = useMemo((): MergeListingGroupInput | null => {
    if (isEmpty(ungroupedListings)) {
      return null;
    }
    return {
      listingGroupId: UngroupedListingGroupId,
      name: ungroupedText,
      viagogoEventId: null,
      viagogoMappingId: null,
      marketplaceSettings: null,
      maxActiveListings: null,
      minActiveListings: null,
      groupingMethods: null,
      groupUndercutSetting: null,
      desiredActiveListings: null,
      deprioritizedQuantities: null,
      targetPercentage: null,
      inheritListingGroupSettings: false,
      maintainPriceStagger: false,
      listingGroupItems: ungroupedListings.map(
        (listing, index) =>
          ({
            listingId: listing.id,
            priority: index + 1,
            groupId: null,
            undercutSetting: null,
          }) as ListingGroupItemInput
      ),
    };
  }, [ungroupedListings, ungroupedText]);

  const enrichedGroups = useMemo(() => {
    if (ungroupedListingGroup === null) {
      return groups;
    }
    return [...groups, ungroupedListingGroup];
  }, [groups, ungroupedListingGroup]);

  return (
    <Tabs.Root
      value={selectedGroup}
      onValueChange={setSelectedGroup}
      defaultValue={selectedGroup}
      className={styles.overrideTabRoot}
    >
      <Tabs.List>
        {enrichedGroups.map((group) => {
          return (
            <Tabs.Trigger
              key={group.listingGroupId}
              value={group.listingGroupId}
            >
              {group.name}
            </Tabs.Trigger>
          );
        })}
      </Tabs.List>
      {enrichedGroups.map((group) => {
        return (
          <Tabs.Content key={group.listingGroupId} value={group.listingGroupId}>
            <GroupListingsPreview
              listingGroup={group}
              eventListings={eventListings}
            />
          </Tabs.Content>
        );
      })}
    </Tabs.Root>
  );
};
